import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Plus, Briefcase, Link2, GraduationCap, Trash2, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useExperiences } from "@/hooks/useExperiences";
import { usePortfolio } from "@/hooks/usePortfolio";
import { useCompletedCourses } from "@/hooks/useCompletedCourses";
import { toast } from "@/hooks/use-toast";

export default function AlunoExperiencia() {
  const navigate = useNavigate();
  const { experiences, isLoading: expLoading, deleteExperience } = useExperiences();
  const { links, isLoading: portfolioLoading, deleteLink } = usePortfolio();
  const { courses, isLoading: coursesLoading, deleteCourse } = useCompletedCourses();

  const isLoading = expLoading || portfolioLoading || coursesLoading;

  const handleDeleteExp = async (id: string) => {
    try {
      await deleteExperience.mutateAsync(id);
      toast({ title: "Experiência removida" });
    } catch {
      toast({ title: "Erro ao remover", variant: "destructive" });
    }
  };

  const handleDeleteLink = async (id: string) => {
    try {
      await deleteLink.mutateAsync(id);
      toast({ title: "Link removido" });
    } catch {
      toast({ title: "Erro ao remover", variant: "destructive" });
    }
  };

  const handleDeleteCourse = async (id: string) => {
    try {
      await deleteCourse.mutateAsync(id);
      toast({ title: "Curso removido" });
    } catch {
      toast({ title: "Erro ao remover", variant: "destructive" });
    }
  };

  if (isLoading) {
    return (
      <div className="page-container flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="page-container">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-foreground">Experiência</h1>
        <p className="text-muted-foreground text-sm mt-1">Suas experiências e aprendizados</p>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-6">
        <Button variant="hero" size="lg" className="w-full" onClick={() => navigate("/aluno/experiencia/nova")}>
          <Plus className="w-5 h-5" />Adicionar experiência
        </Button>
      </motion.div>

      {/* Experiências */}
      <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-6">
        <h2 className="font-heading text-base font-semibold text-foreground mb-3">Experiências</h2>
        {experiences.length > 0 ? (
          <div className="space-y-3">
            {experiences.map((exp) => (
              <div key={exp.id} className="card-interactive">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <Briefcase className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-foreground">{exp.title}</p>
                        {exp.exp_type && <Badge variant="outline" className="text-xs capitalize">{{ clt: "CLT", autonomo: "Autônomo", formal: "Formal", freela: "Freela", project: "Projeto", voluntariado: "Voluntariado" }[exp.exp_type] ?? exp.exp_type}</Badge>}
                        {exp.org && <p className="text-xs text-muted-foreground">{exp.org}</p>}
                      </div>
                      <Button variant="ghost" size="icon-sm" onClick={() => handleDeleteExp(exp.id)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                    {exp.description && <p className="text-sm text-muted-foreground mt-2">{exp.description}</p>}
                    {exp.learning_outcome && (
                      <div className="mt-3 p-3 bg-accent/10 rounded-lg">
                        <p className="text-xs font-medium text-accent-foreground mb-1">💡 O que aprendi</p>
                        <p className="text-sm text-muted-foreground">{exp.learning_outcome}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-muted/50 rounded-2xl p-6 text-center">
            <Briefcase className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">Nenhuma experiência adicionada ainda</p>
          </div>
        )}
      </motion.section>

      {/* Portfólio */}
      <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-base font-semibold text-foreground">Portfólio</h2>
          <Button variant="outline" size="sm" onClick={() => navigate("/aluno/experiencia/portfolio/novo")}>
            <Plus className="w-4 h-4" />Adicionar link
          </Button>
        </div>
        {links.length > 0 ? (
          <div className="space-y-2">
            {links.map((item) => (
              <div key={item.id} className="flex items-center gap-3 p-3 bg-card rounded-xl border border-border">
                <Link2 className="w-5 h-5 text-primary" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{item.url}</p>
                  <p className="text-xs text-muted-foreground">{item.platform}</p>
                </div>
                <Button variant="ghost" size="icon-sm" onClick={() => handleDeleteLink(item.id)}>
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground p-3 bg-muted/50 rounded-xl text-center">Nenhum link de portfólio</p>
        )}
      </motion.section>

      {/* Cursos */}
      <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-base font-semibold text-foreground">Cursos Realizados</h2>
          <Button variant="outline" size="sm" onClick={() => navigate("/aluno/experiencia/cursos/novo")}>
            <Plus className="w-4 h-4" />Adicionar
          </Button>
        </div>
        {courses.length > 0 ? (
          <div className="space-y-2">
            {courses.map((curso) => (
              <div key={curso.id} className="flex items-center gap-3 p-3 bg-card rounded-xl border border-border">
                <GraduationCap className="w-5 h-5 text-success" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{curso.course_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {[curso.provider, curso.year_label].filter(Boolean).join(" • ")}
                  </p>
                </div>
                <Button variant="ghost" size="icon-sm" onClick={() => handleDeleteCourse(curso.id)}>
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground p-3 bg-muted/50 rounded-xl text-center">Nenhum curso adicionado</p>
        )}
      </motion.section>
    </div>
  );
}
